import {
  useDataProvider,
  useGetIdentity,
  useInvalidate,
  useNotification,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import {
  AREA_AUDIT_KINDS,
  type AreaAuditFindingRow,
  type AreaAuditKind,
  type AreaAuditSeverity,
  type AreaAuditStatus,
  type VenueAuditRunResponse,
} from "@/types/tina";

/** Cache key root for everything on this tab that is not the Refine table itself. */
export const AREA_AUDIT_QUERY_ROOT = "tina-area-audit";

export type AreaAuditKindCounts = Record<AreaAuditKind, number>;

export interface AreaAuditCountScope {
  status: AreaAuditStatus;
  severity: AreaAuditSeverity | null;
  city: string | null;
}

/**
 * The writes and the one derived read the Area tab needs — `useVenueAuditRepository`'s
 * shape, pointed at `/area-audit/*`: the data provider's `custom`, Sentry breadcrumbs, and
 * errors surfaced through `toHumanErrorMessage`.
 *
 * `/area-audit/findings` itself is read by `useTable` through the `tina-area-audit`
 * resource, not from here — the table owns pagination and `total`.
 */
export const useAreaAuditRepository = () => {
  const { custom } = useDataProvider()("tina");
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  const { data: identity } = useGetIdentity<IIdentity>();

  /**
   * The signed-in curator's Firebase uid. The dismiss route authenticates the SERVICE,
   * not the person, so the body is the only place the actor comes from; an unloaded
   * identity blocks the dismissal rather than inventing an actor (see
   * `useVenueAuditRepository`).
   */
  const dismissedBy = identity?.id;

  /** Mark this tab's rows and its count strip stale, so both refetch. */
  function invalidateFindings(): void {
    void invalidate({
      dataProviderName: "tina",
      resource: "tina-area-audit",
      invalidates: ["list"],
    });
    void queryClient.invalidateQueries({ queryKey: [AREA_AUDIT_QUERY_ROOT] });
  }

  async function dismiss(
    findingId: string,
    note: string | null,
  ): Promise<AreaAuditFindingRow | null> {
    if (!dismissedBy) {
      open?.({
        type: "error",
        message: "Cannot dismiss yet",
        description:
          "Your account has not finished loading, and a dismissal has to be attributable. Reload the page and try again.",
      });
      return null;
    }
    Sentry.addBreadcrumb({
      category: "tina-area-audit.dismiss",
      message: "Dismissing an area-audit finding",
      level: "info",
      data: { findingId, hasNote: Boolean(note) },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: { dismissed_by: dismissedBy, note: note || null },
        meta: {
          urlPath: `area-audit/findings/${encodeURIComponent(findingId)}/dismiss`,
        },
      });
      invalidateFindings();
      open?.({
        type: "success",
        message: "Finding dismissed",
        description: "It will not come back unless its evidence changes.",
      });
      return response.data as AreaAuditFindingRow;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to dismiss the finding",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  /**
   * How many findings each kind holds inside the current status/severity/city view: one
   * `limit=1` read per kind, reading `total` off each — the route counts over the same
   * WHERE it pages, so each total is exact, never a tally of the loaded page.
   *
   * Rejects when the route does, so a 404 (Tina not deployed yet) reaches the caller's
   * query as an error rather than as eight zeroes that would read as "nothing to fix".
   */
  async function countsByKind({
    status,
    severity,
    city,
  }: AreaAuditCountScope): Promise<AreaAuditKindCounts> {
    const totals = await Promise.all(
      AREA_AUDIT_KINDS.map(async (kind) => {
        const response = await custom!({
          url: "",
          method: "get",
          query: {
            status,
            kind,
            limit: 1,
            offset: 0,
            ...(severity ? { severity } : {}),
            ...(city ? { city } : {}),
          },
          meta: { urlPath: "area-audit/findings" },
        });
        const body = response.data as { total?: number };
        return [kind, body.total ?? 0] as const;
      }),
    );
    return Object.fromEntries(totals) as AreaAuditKindCounts;
  }

  /**
   * START the venue audit job now — the area step runs inside it, so this is the only way
   * to re-run the area checks. There is no `/area-audit/run`. Same route, same answer and
   * the same "started, not finished" honesty as the Venue tab's button: it returns a Job
   * execution, never counts.
   */
  async function runNow(): Promise<VenueAuditRunResponse> {
    Sentry.addBreadcrumb({
      category: "tina-area-audit.run",
      message: "Starting the venue audit job for its area step",
      level: "info",
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: {},
        meta: { urlPath: "venue-audit/run" },
      });
      const outcome = response.data as VenueAuditRunResponse;
      invalidateFindings();
      if (outcome.kind === "dispatched") {
        open?.({
          type: "success",
          message: "Audit started",
          description:
            "The area step runs after the venue checks, so it takes a while. Refresh this page when the run is done — nothing here updates on its own.",
        });
      } else if (outcome.kind === "noop") {
        open?.({
          type: "error",
          message: "Audit not started",
          description:
            "The audit job is not configured on this environment, so there was nothing to start.",
        });
      } else {
        open?.({
          type: "error",
          message: "Failed to start the audit",
          description:
            outcome.error_message ??
            "The audit job could not be started and gave no reason.",
        });
      }
      return outcome;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to start the audit",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  return { dismiss, countsByKind, runNow, dismissedBy };
};
